"use client";

import React, { useRef, useLayoutEffect, useState, useCallback, useEffect } from "react";

import styled from "styled-components";
import AutosizeTextArea from "react-textarea-autosize";

import Button from "../Button";
import Flex from "../Flex";
import Box, { BoxProps } from "../Box";
import Text from "../Text";

import useClipboard from "../../hooks/useClipboard";
import focusEndOfInput, { focusAllOfInput } from "../../helpers/focusEndOfInput";
import { getColor, getFont } from "../utils";
import codeLangs from "../../constants/codeLangs";
import useSubmitTextKeys from "../../hooks/useSubmitTextKeys";
import useCodeHighlighting from "../../hooks/useCodeHighlighting";
import { AnyObject, Item } from "../../models";
import useBoolean from "../../hooks/useBoolean";
import { PopupMenu } from "..";

export const editorTestId = "gallery-code-snippet-editor";
export const wrapperTestId = "gallery-code-snippet-wrapper";
export const parentTestId = "gallery-code-snippet-parent";
export const selectTestId = "gallery-code-snippet-select";

type Props = {
  item: Item;
  selected?: boolean;
  canEdit?: boolean;
  isEditing?: boolean;
  hasDefaultValue?: boolean;
  onOverflowSelected?: (item: Item) => void;
  onBeginEditing?: (itemId: string) => void;
  onFinishEditing?: (item: Item, data: AnyObject) => void;
  onClick?: (e: React.MouseEvent) => void;
} & BoxProps;

type WrapperProps = Omit<Props, "item">;

const Wrapper = styled(Box).attrs<WrapperProps>(props => {
  return {
    border: `1px solid ${props.selected && props.canEdit ? "transparent" : "border"}`,
    background: "grayLightest",
    borderRadius: "default",
    width: "100%",
  };
})<WrapperProps>`
  .show-on-hover {
    opacity: 0;
  }
  &:hover {
    .show-on-hover {
      opacity: 1;
    }
  }
`;

const SelectionWrapper = styled(Box).attrs<WrapperProps>(props => {
  return {
    borderRadius: "default",
    m: "-2px",
    border: `2px solid ${
      props.selected ? getColor(props.theme.primaryColor, "primary") : "transparent"
    }`,
  };
})<WrapperProps>``;

const ActionContainer = styled(Flex).attrs({
  position: "absolute",
  top: "4px",
  right: "4px",
  gap: "8px",
  transition: "opacity 0.1s ease-in-out",
})`
  button {
    box-shadow: 0 1px 3px 0 rgba(36, 36, 37, 0.12) !important;
  }
`;

const OverflowButton = styled(Button).attrs(() => {
  return {
    className: "action-button",
    size: "small",
    buttonStyle: "secondary",
    boxShadow: "button",
  };
})``;

const Pre = styled(Text).attrs({
  as: "pre",
  p: "none",
  background: "transparent",
})``;

const Code = styled(Text).attrs({
  as: "code",
  font: "gallery.code",
  p: "none",
})`
  word-break: break-word;
`;

type EditorWrapperProps = {
  canEdit: boolean;
} & BoxProps;
const EditorWrapper = styled(Box).attrs<EditorWrapperProps>(props => {
  return {
    background: "transparent",
    minHeight: props.minHeight,
    width: "100%",
  };
})<EditorWrapperProps>`
  ${props =>
    props.canEdit &&
    `
    cursor: text;
  `}
`;

const Textarea = styled(AutosizeTextArea)`
  min-height: 18px;
  padding: 0;
  background: transparent;
  border: none;
  width: 100%;
  ${getFont(null, "gallery.code")};
  color: ${getColor("grayDarkest")};
  resize: none;

  /**
  * Safari, Chrome kill textarea height jump
  */
  vertical-align: top;

  /*
  * Safari kill x margin
  */
  margin: 0;

  &:focus {
    outline: none;
  }
`;

const GalleryCodeSnippet: React.FC<Props> = ({
  item,
  selected,
  canEdit,
  isEditing = false,
  hasDefaultValue,
  onBeginEditing,
  onFinishEditing,
  onClick,
  onOverflowSelected,
  ...rest
}) => {
  const {
    data: { content, codeLanguage },
  } = item;
  const [editValue, setEditValue] = useState(content);
  const [minHeight, setMinHeight] = useState(null);

  // Refs
  const codeRef = useCodeHighlighting(content, codeLanguage);
  const editorRef = useRef<HTMLTextAreaElement>(null);

  const { onCopy, hasCopied } = useClipboard(content);

  const wrapperId = item.uuid.concat("-code-snippet");

  const overflowSelected = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      onOverflowSelected(item);
    },
    [item, onOverflowSelected]
  );

  const beginEditing = useCallback(() => {
      if (onBeginEditing) onBeginEditing(item.uuid);
    }, [item, onBeginEditing]),
    finishEditing = useCallback(() => {
      if (!isEditing || !canEdit || !onFinishEditing) return;

      // Do not submit if the original content hasn't changed
      const val = editValue.trim();
      if (content.trim() === val) {
        return onFinishEditing(item, null);
      }
      const data = {
        data: {
          content: val,
          // original code lang
          codeLanguage,
        },
      };
      onFinishEditing(item, data);
    }, [isEditing, canEdit, onFinishEditing, editValue, content, codeLanguage, item]);

  const updateLanguage = useCallback(
    value => {
      if (!canEdit || !onFinishEditing) return;
      // Do not submit if the lang hasn't changed
      if (codeLanguage === value) return;
      const data = {
        data: {
          // current item text content
          content: content,
          codeLanguage: value,
        },
      };
      onFinishEditing(item, data);
    },
    [canEdit, onFinishEditing, codeLanguage, content, item]
  );

  const onKeyDown = useSubmitTextKeys(true);

  /**
   * Rehydrate the editor state vars each time the item in state updates
   * or the editor mode is toggled
   *
   * Focus and place cursor at the end of the textara content when entering edit mode
   */
  useEffect(() => {
    setEditValue(content);
    if (isEditing && editorRef.current) {
      (hasDefaultValue ? focusAllOfInput : focusEndOfInput)(editorRef.current);
    }
  }, [content, isEditing, hasDefaultValue]);

  /**
   * Set the min-height of the editor container to the initial
   * height to prevent flashes of surrounding content when toggling modes
   */
  useLayoutEffect(() => {
    const heightTimeout = setTimeout(() => {
      const wrapperEl = document.getElementById(wrapperId);
      if (!wrapperEl) return;
      if (isEditing) return setMinHeight(null);
      setMinHeight(`${wrapperEl.clientHeight}px`);
    }, 100);
    return () => clearTimeout(heightTimeout);
  }, [wrapperId, editValue, item, isEditing]);

  const renderPre = () => {
    return (
      <Pre>
        <Code ref={codeRef}>{content}</Code>
      </Pre>
    );
  };

  /**
   * Watch for shift+enter or meta+enter key tp update snippet
   */

  const lanugageMenuId = `language-menu-${item.uuid}`;
  const [isLanguageMenuOpen, openLanguageMenu, closeLanguageMenu] = useBoolean(false);
  function renderLanguageMenu() {
    if (!isLanguageMenuOpen) return null;
    return (
      <PopupMenu close={closeLanguageMenu} source={lanugageMenuId}>
        {codeLangs.map(style => {
          return (
            <PopupMenu.Item
              key={style}
              data-testid={`code-language-${style}`}
              checked={codeLanguage === style}
              title={style}
              onClick={() => {
                updateLanguage(style);
                closeLanguageMenu();
              }}
            />
          );
        })}
      </PopupMenu>
    );
  }

  function renderTitle() {
    if (canEdit)
      return (
        <Button
          data-testid={selectTestId}
          buttonStyle="tertiary"
          themeOverrides={{
            primaryColor: "darkOverlay",
            primaryColorDark: "darkOverlay",
          }}
          icon="action.expand"
          text={codeLanguage ?? "Auto"}
          onClick={openLanguageMenu}
          fontStyle="ui.smallBold"
          size="small"
          dataPopupSource={lanugageMenuId}
        />
      );

    if (!codeLanguage || codeLanguage?.toLowerCase() === "auto") return null;

    return <Text font="ui.smallBold">{codeLanguage ?? "Auto"}</Text>;
  }

  const wrapperProps = {
    canEdit,
    onDoubleClick: canEdit ? beginEditing : undefined,
  };
  return (
    <Wrapper selected={selected} {...rest} data-testid={parentTestId}>
      {renderLanguageMenu()}
      <SelectionWrapper selected={selected} onClick={canEdit ? onClick : null}>
        <Flex flexDirection="column" gap="10px" p="10px 8px" alignItems="flex-start">
          {renderTitle()}
          <EditorWrapper
            id={wrapperId}
            data-testid={wrapperTestId}
            minHeight={minHeight}
            {...wrapperProps}>
            {isEditing ? (
              <Textarea
                ref={editorRef}
                data-testid={editorTestId}
                aria-label="Update code snippet"
                onBlur={finishEditing}
                onKeyDown={onKeyDown}
                onChange={e => setEditValue((e.target as AnyObject).value as string)}
                value={editValue}
                maxRows={1000}
              />
            ) : (
              renderPre()
            )}
          </EditorWrapper>
        </Flex>
      </SelectionWrapper>
      <ActionContainer className="show-on-hover">
        <OverflowButton
          dataPopupSource={`overflow-menu-${item.uuid}`}
          buttonStyle="secondary"
          icon="navigation.overflow"
          onClick={overflowSelected}
        />
        <Button onClick={onCopy} size="small" text={hasCopied ? "Copied" : "Copy"} />
      </ActionContainer>
    </Wrapper>
  );
};

export default GalleryCodeSnippet;
